import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, DollarSign, Clock, Tag, Save, X, ToggleLeft, ToggleRight, Upload, Image } from 'lucide-react';
import { useTalentAuth } from '../../contexts/TalentAuthContext';
import axios from 'axios';
import Swal from 'sweetalert2';
import './TalentServices.css';
import { useNavigate } from 'react-router-dom';
import { Crown } from 'lucide-react';
import { normalizeImageUrl } from '../../utils/api';

interface Service {
  id: number;
  title: string;
  description: string;
  price: number;
  duration: string;
  category: string;
  isActive: boolean;
  image?: string;
  galleryImages?: string[];
  portfolio?: string[];
  approval_status?: 'pending' | 'approved' | 'rejected';
}

interface Category {
  id: number;
  name: string;
  description: string;
  icon: string;
  skills?: any[];
}

// Create API instance with proper configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('skillseek_talent_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const TalentServices: React.FC = () => {
  const { talent } = useTalentAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [mainImageFile, setMainImageFile] = useState<File | null>(null);
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [existingGalleryImages, setExistingGalleryImages] = useState<string[]>([]);
  const [portfolioFiles, setPortfolioFiles] = useState<File[]>([]);
  const [existingPortfolio, setExistingPortfolio] = useState<string[]>([]);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [newCategoryData, setNewCategoryData] = useState({ name: '', description: '', icon: 'Briefcase' });
  const [categoryError, setCategoryError] = useState<string | null>(null);
  const [savingCategory, setSavingCategory] = useState(false);
  const [membershipLoading, setMembershipLoading] = useState(true);
  const [membershipStatus, setMembershipStatus] = useState<{
    hasPremium: boolean;
    membershipTier: string;
    serviceLimit: number;
    servicesUsed: number;
    hasPaidMembership: boolean;
    transactionActive: boolean;
  }>({
    hasPremium: false,
    membershipTier: 'basic',
    serviceLimit: 0,
    servicesUsed: 0,
    hasPaidMembership: false,
    transactionActive: false
  });
  const [previewImages, setPreviewImages] = useState<{
    main: string | null;
    gallery: string[];
    portfolio: string[];
  }>({
    main: null,
    gallery: [],
    portfolio: []
  });
  const [displayHourlyRate, setDisplayHourlyRate] = useState(false);

  const [formData, setFormData] = useState<Omit<Service, 'id'> & { portfolio?: string[] }>({
    title: '',
    description: '',
    price: 0,
    duration: '1 hour',
    category: '',
    isActive: true,
    image: '',
    galleryImages: [],
    portfolio: []
  });

  const durationOptions = [
    '30 minutes',
    '1 hour',
    '2 hours',
    '3 hours',
    '4 hours',
    'Half day (5 hours)',
    'Full day (8 hours)',
    'Custom'
  ];

  // Remove the hardcoded categoryOptions array

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get('/talent/auth/services');

      if (response.data.success) {
        setServices(response.data.services);
      } else {
        throw new Error(response.data.message || 'Failed to fetch services');
      }
    } catch (error: any) {
      console.error('Error fetching services:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/customers/categories');

      if (response.data.success) {
        setCategories(response.data.categories);
      } else {
        throw new Error(response.data.message || 'Failed to fetch categories');
      }
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      // Don't set error state for categories as it's not critical
    }
  };

  // Fetch membership status
  const fetchMembershipStatus = async () => {

    try {

      // Get membership info from the existing endpoint
      const membershipResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/membership/my-membership`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('skillseek_talent_token')}`
        }
      });
      const membershipData = await membershipResponse.json();

      // Then check for transaction-based membership
      const transactionResponse = await api.get('/membership/transaction-check');
      console.log('FULL transaction response:', JSON.stringify(transactionResponse.data, null, 2));


      // Determine the actual membership status based on both responses
      const hasPaidMembership = transactionResponse.data.success && transactionResponse.data.hasPaidMembership;
      const transactionActive = hasPaidMembership &&
        transactionResponse.data.transaction &&
        transactionResponse.data.transaction.isActive;

      console.log('Transaction response details:', {
        success: transactionResponse.data.success,
        hasPaidMembership: transactionResponse.data.hasPaidMembership,
        transaction: transactionResponse.data.transaction,
        calculatedHasPaidMembership: hasPaidMembership,
        calculatedTransactionActive: transactionActive
      });

      // Use transaction data if available, otherwise use regular membership data
      setMembershipStatus({
        hasPremium: hasPaidMembership,
        membershipTier: hasPaidMembership ?
          transactionResponse.data.transaction.tierDisplayName :
          (membershipData.success ? membershipData.membership.membership_tier_display : 'Basic'),
        serviceLimit: hasPaidMembership ?
          transactionResponse.data.serviceUsage.limit :
          (membershipData.success ? membershipData.membership.service_limit : 3),
        servicesUsed: hasPaidMembership ?
          transactionResponse.data.serviceUsage.count :
          (membershipData.success ? membershipData.membership.services_used : 0),
        hasPaidMembership,
        transactionActive
      });

      console.log('Membership status updated with transaction check:', {
        hasPaidMembership,
        transactionActive,
        membershipData: membershipData.success ? membershipData.membership : null,
        transactionData: transactionResponse.data,
        transactionResponseFull: transactionResponse
      });
    } catch (error) {
      console.error('Error fetching membership status:', error);
      // Set default values in case of error
      setMembershipStatus({
        hasPremium: false,
        membershipTier: 'Basic',
        serviceLimit: 3,
        servicesUsed: 0,
        hasPaidMembership: false,
        transactionActive: false
      });
    } finally {
      setMembershipLoading(false); // ← yeh add karo
    }
  };

  useEffect(() => {
    if (!talent) {
      setLoading(false);
      setError('Please log in to manage your services');
      return;
    }
    fetchServices();
    fetchCategories();
    fetchMembershipStatus();
  }, [talent]);

  const handleInputChange = (field: keyof Omit<Service, 'id'>, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCategoryChange = (value: string) => {
    if (value === '__custom__') {
      setShowAddCategoryModal(true);
    } else {
      setFormData(prev => ({ ...prev, category: value }));
    }
  };

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newCategoryData.name.trim()) {
      setCategoryError('Category name is required');
      return;
    }

    try {
      setSavingCategory(true);
      setCategoryError(null);

      const response = await api.post('/talent/auth/categories', {
        name: newCategoryData.name.trim(),
        description: newCategoryData.description.trim(),
        icon: newCategoryData.icon || 'Briefcase',
        status: 'active'
      });

      if (response.data.success) {
        // Refresh categories list
        await fetchCategories();

        // Auto-select the newly created category
        setFormData(prev => ({ ...prev, category: response.data.category.name }));

        // Close modal and reset form
        setShowAddCategoryModal(false);
        setNewCategoryData({ name: '', description: '', icon: 'Briefcase' });

        // Show success message
        await Swal.fire({
          title: 'Success!',
          text: 'Category created successfully',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745',
          timer: 2000
        });
      } else {
        throw new Error(response.data.message || 'Failed to create category');
      }
    } catch (error: any) {
      console.error('Error creating category:', error);
      setCategoryError(error.response?.data?.message || error.message || 'Failed to create category');
    } finally {
      setSavingCategory(false);
    }
  };

  const resetCategoryModal = () => {
    setShowAddCategoryModal(false);
    setNewCategoryData({ name: '', description: '', icon: 'Briefcase' });
    setCategoryError(null);
  };

  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setMainImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setPreviewImages(prev => ({ ...prev, main: previewUrl }));
    }
  };

  const handleGalleryImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    if (files.length > 0) {


      const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
      if (oversizedFiles.length > 0) {
        Swal.fire({
          title: 'File Too Large!',
          text: `${oversizedFiles[0].name} is larger than 3MB. Please choose a smaller file.`,
          icon: 'warning',
          confirmButtonColor: '#dc3545',
        });
        return;
      }

      setGalleryFiles(prev => [...prev, ...files]);
      const newPreviews = files.map(file => URL.createObjectURL(file));
      setPreviewImages(prev => ({
        ...prev,
        gallery: [...prev.gallery, ...newPreviews]
      }));
    }
  };

  const removeGalleryImage = (index: number) => {
    setGalleryFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewImages(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
  };

  const removeExistingGalleryImage = async (imageName: string, serviceId?: number) => {
    // Remove from state
    setExistingGalleryImages(prev => prev.filter(img => img !== imageName));

    // If editing a service and it has an ID, also delete from backend
    if (serviceId) {
      try {
        await api.delete(`/talent/auth/services/${serviceId}/gallery-image`, {
          data: { imageName }
        });
      } catch (error: any) {
        console.error('Error deleting gallery image:', error);
        // Optionally show error to user
        Swal.fire({
          title: 'Warning',
          text: 'Image removed from form but may not be deleted from server',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  const handlePortfolioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    if (files.length > 0) {

      const oversizedFiles = files.filter(file => file.size > 10* 1024 * 1024);
      if (oversizedFiles.length > 0) {
        Swal.fire({
          title: 'File Too Large!',
          text: `${oversizedFiles[0].name} is larger than 3MB. Please choose a smaller file.`,
          icon: 'warning',
          confirmButtonColor: '#dc3545'
        });
        return;
      }

      setPortfolioFiles(prev => [...prev, ...files]);
      const newPreviews = files.map(file => URL.createObjectURL(file));
      setPreviewImages(prev => ({
        ...prev,
        portfolio: [...prev.portfolio, ...newPreviews]
      }));
    }
  };
  const removePortfolioItem = (index: number) => {
    setPortfolioFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewImages(prev => ({
      ...prev,
      portfolio: prev.portfolio.filter((_, i) => i !== index)
    }));
  };

  const removeExistingPortfolioItem = async (imageName: string, serviceId?: number) => {
    setExistingPortfolio(prev => prev.filter(img => img !== imageName));

    if (serviceId) {
      try {
        await api.delete(`/talent/auth/services/${serviceId}/gallery-image`, {
          data: { imageName }
        });
      } catch (error: any) {
        console.error('Error deleting portfolio item:', error);
        Swal.fire({
          title: 'Warning',
          text: 'Item removed from form but may not be deleted from server',
          icon: 'warning',
          confirmButtonText: 'OK'
        });
      }
    }
  };

  const uploadImages = async () => {
    if (!mainImageFile && galleryFiles.length === 0 && portfolioFiles.length === 0) {
      return { image: null, gallery_images: [], portfolio: [] };
    }

    const formData = new FormData();

    if (mainImageFile) {
      formData.append('image', mainImageFile);
    }

    galleryFiles.forEach(file => {
      formData.append('gallery_images', file);
    });

    portfolioFiles.forEach(file => {
      formData.append('portfolio', file);
    });

    try {
      setUploadingImages(true);
      const response = await api.post('/talent/auth/services/upload-images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        return response.data.files;
      } else {
        throw new Error(response.data.message || 'Failed to upload images');
      }
    } catch (error: any) {
      console.error('Error uploading images:', error);
      throw error;
    } finally {
      setUploadingImages(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      // First check if user has a paid membership
      // TEMPORARY BYPASS: Commented out frontend membership checks
      /* 
      if (!editingService) {
        try {
          // Check for transaction-based membership
          const transactionResponse = await api.get('/membership/transaction-check');
          const hasPaidMembership = transactionResponse.data.success && transactionResponse.data.hasPaidMembership;
          const transactionActive = hasPaidMembership && 
            transactionResponse.data.transaction && 
            transactionResponse.data.transaction.isActive;
          
          if (!hasPaidMembership || !transactionActive) {
            // Show SweetAlert for no paid membership
            await Swal.fire({
              title: 'Membership Required!',
              html: `
                <div class="text-center">
                  <div class="mb-3">
                    <i class="fas fa-crown text-warning" style="font-size: 3rem;"></i>
                  </div>
                  <p class="mb-2">You need an active paid membership to add service listings.</p>
                  <div class="alert alert-info">
                    <strong>Premium Membership</strong> is required to add services
                  </div>
                  <p class="text-muted">Upgrade to <strong>Premium</strong> for unlimited services and enhanced features!</p>
                </div>
              `,
              icon: 'warning',
              showCancelButton: true,
              confirmButtonText: '<i class="fas fa-crown me-2"></i>Upgrade Now',
              cancelButtonText: 'Cancel',
              confirmButtonColor: '#ffc107',
              cancelButtonColor: '#6c757d',
              customClass: {
                popup: 'swal-upgrade-popup',
                confirmButton: 'btn btn-warning',
                cancelButton: 'btn btn-secondary'
              }
            }).then((result) => {
              if (result.isConfirmed) {
                // Navigate to membership upgrade page
                navigate('/talent/membership');
              }
            });
            
            setLoading(false);
            return; // Exit the function early
          }

          // If they have a paid membership, still check service limits
          if (!transactionResponse.data.serviceUsage.canAddService) {
            // Show SweetAlert for service limit reached
            await Swal.fire({
              title: 'Service Limit Reached!',
              html: `
                <div class="text-center">
                  <div class="mb-3">
                    <i class="fas fa-crown text-warning" style="font-size: 3rem;"></i>
                  </div>
                  <p class="mb-2">You've reached your service limit with your <strong>${transactionResponse.data.transaction.tierDisplayName}</strong> membership.</p>
                  <div class="alert alert-info">
                    <strong>${transactionResponse.data.serviceUsage.count}/${transactionResponse.data.serviceUsage.limit === -1 ? '∞' : transactionResponse.data.serviceUsage.limit}</strong> services used
                  </div>
                  <p class="text-muted">Contact support for assistance with increasing your service limit.</p>
                </div>
              `,
              icon: 'warning',
              confirmButtonText: 'OK',
              confirmButtonColor: '#6c757d'
            });
            
            setLoading(false);
            return; // Exit the function early
          }
        } catch (limitError: any) {
          console.error('Membership check error:', limitError);
          
          // Show error alert for check failure
          await Swal.fire({
            title: 'Error',
            text: limitError.message || 'Unable to check membership status. Please try again.',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#dc3545'
          });
          
          setLoading(false);
          return;
        }
      }
      */

      // Upload images first if any
      const uploadedImages = await uploadImages();

      // Prepare form data with images
      // Merge existing gallery images with newly uploaded ones
      const mergedGalleryImages = [
        ...existingGalleryImages, // Keep existing images
        ...(uploadedImages.gallery_images || []) // Add new uploaded images
      ];

      const mergedPortfolioItems = [
        ...existingPortfolio, // Keep existing portfolio
        ...(uploadedImages.portfolio || []) // Add new uploaded portfolio items
      ];

      const serviceData = {
        ...formData,
        image: uploadedImages.image || formData.image,
        gallery_images: mergedGalleryImages,
        portfolio: mergedPortfolioItems
      };

      const endpoint = editingService
        ? `/talent/auth/services/${editingService.id}`
        : '/talent/auth/services';

      const method = editingService ? 'put' : 'post';

      const response = await api[method](endpoint, serviceData);

      if (response.data.success) {
        await fetchServices();
        resetForm();
        // Clean up preview URLs
        if (previewImages.main) URL.revokeObjectURL(previewImages.main);
        previewImages.gallery.forEach(url => URL.revokeObjectURL(url));
        previewImages.portfolio.forEach(url => URL.revokeObjectURL(url));
        setMainImageFile(null);
        setGalleryFiles([]);
        setPortfolioFiles([]);
        setPreviewImages({ main: null, gallery: [], portfolio: [] });
        setExistingGalleryImages([]);
        setExistingPortfolio([]);

        // Show appropriate message based on approval status
        if (response.data.isPending) {
          await Swal.fire({
            title: 'Service Created as Draft',
            html: `
              <p><strong>Your service has been created successfully!</strong></p>
              <div class="alert" style="background: #fff3cd; border: 1px solid #ffc107; padding: 12px; border-radius: 8px; margin-top: 12px;">
                <p style="margin: 0; color: #856404;">⏳ This service is <strong>pending approval</strong> because it uses a newly created category.</p>
                <p style="margin: 8px 0 0 0; color: #856404;">You'll be notified once an admin reviews and approves it.</p>
              </div>
            `,
            icon: 'info',
            confirmButtonText: 'Got it',
            confirmButtonColor: '#28a745'
          });
        } else {
          await Swal.fire({
            title: 'Success!',
            text: editingService ? 'Service updated successfully' : 'Service created successfully',
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#28a745',
            timer: 2000
          });
        }
      } else {
        throw new Error(response.data.message || 'Failed to save service');
      }
    } catch (error: any) {
      console.error('Error saving service:', error);

      // Check if it's a plan access restriction error from the API
      if (error.response?.status === 403) {
        const errorMsg = error.response?.data?.message || 'You do not have access to add service listings under your current plan. Please upgrade your plan.';

        await Swal.fire({
          title: 'Access Restricted!',
          html: `
            <div class="text-center">
              <div class="mb-3">
                <i class="fas fa-crown text-warning" style="font-size: 3rem;"></i>
              </div>
              <p class="mb-2 fw-bold text-dark">${errorMsg}</p>
              <div class="alert alert-warning mt-3">
                Upgrade your subscription plan to unlock full application service listings and features!
              </div>
            </div>
          `,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Upgrade Plan',
          cancelButtonText: 'Cancel',
          confirmButtonColor: '#185FA5',
          cancelButtonColor: '#6c757d'
        }).then((result) => {
          if (result.isConfirmed) {
            navigate('/talent/membership/select');
          }
        });
      } else {
        // Show generic error for other types of errors
        await Swal.fire({
          title: 'Error',
          text: error.response?.data?.message || error.message || 'Failed to save service',
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#dc3545'
        });
      }

      setError(error.response?.data?.message || error.message || 'Failed to save service');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service: Service) => {
    setEditingService(service);
    setFormData({
      title: service.title,
      description: service.description,
      price: service.price,
      duration: service.duration,
      category: service.category,
      isActive: service.isActive,
      image: service.image || '',
      galleryImages: service.galleryImages || [],
      portfolio: service.portfolio || []
    });
    // Set display hourly rate checkbox based on whether price is set
    setDisplayHourlyRate(service.price > 0);
    // Set existing gallery images and portfolio items
    setExistingGalleryImages(service.galleryImages || []);
    setExistingPortfolio(service.portfolio || []);
    setShowAddModal(true);
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: 'Confirm Delete',
      text: 'Are you sure you want to delete this service?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d'
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      setError(null);

      const response = await api.delete(`/talent/auth/services/${id}`);

      if (response.data.success) {
        await fetchServices();
        Swal.fire({
          title: 'Success!',
          text: 'Service deleted successfully',
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745'
        });
      } else {
        throw new Error(response.data.message || 'Failed to delete service');
      }
    } catch (error: any) {
      console.error('Error deleting service:', error);
      setError(error.response?.data?.message || error.message || 'Failed to delete service');
      Swal.fire({
        title: 'Error!',
        text: error.response?.data?.message || error.message || 'Failed to delete service',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (service: Service) => {
    try {
      setLoading(true);
      setError(null);

      // Only send the required fields for updating
      // Don't send galleryImages to avoid overwriting them
      const updatedService = {
        title: service.title,
        description: service.description,
        price: service.price,
        duration: service.duration,
        category: service.category,
        isActive: !service.isActive,
        image: service.image || ''
        // Intentionally not including galleryImages to preserve them
      };

      const response = await api.put(`/talent/auth/services/${service.id}`, updatedService);

      if (response.data.success) {
        await fetchServices();
      } else {
        throw new Error(response.data.message || 'Failed to update service');
      }
    } catch (error: any) {
      console.error('Error toggling service status:', error);
      setError(error.response?.data?.message || error.message || 'Failed to update service');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      price: 0,
      duration: '1 hour',
      category: '',
      isActive: true,
      image: '',
      galleryImages: [],
      portfolio: []
    });
    setDisplayHourlyRate(false);
    setEditingService(null);
    setShowAddModal(false);

    // Clean up image state
    if (previewImages.main) URL.revokeObjectURL(previewImages.main);
    previewImages.gallery.forEach(url => URL.revokeObjectURL(url));
    previewImages.portfolio.forEach(url => URL.revokeObjectURL(url));
    setMainImageFile(null);
    setGalleryFiles([]);
    setPortfolioFiles([]);
    setPreviewImages({ main: null, gallery: [], portfolio: [] });
    setExistingGalleryImages([]);
    setExistingPortfolio([]);
  };

  if (!talent) {
    return (
      <div className="services-unauthorized">
        <div className="unauthorized-content">
          <h2>Access Denied</h2>
          <p>Please log in to manage your services</p>
          <div className="unauthorized-actions">
            <button onClick={() => navigate('/talent/login')} className="login-btn">
              Log In
            </button>
            <button onClick={() => navigate('/talent/register')} className="register-btn">
              Register as Talent
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !services.length) {
    return (
      <div className="services-loading">
        <div className="loading-spinner"></div>
        <p>Loading services...</p>
      </div>
    );
  }

  return (
    <div className="talent-services">
      {/* Header */}
      <div className="services-header">
        <div>
          <h1>My Services</h1>
          <p>Manage your service offerings and pricing</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="add-service-btn">
          <Plus size={20} />
          Add New Service
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Membership Status Banner */}
      {/* Membership Status Banner */}
      {!membershipLoading && (
        <>
          {!membershipStatus.hasPaidMembership ? (
            <div className="membership-banner">
              <div className="banner-content">
                <Crown size={24} />
                <div className="banner-text">
                  <h3>Free Account Limitations</h3>
                  <p>Your services will appear blurred to customers until you upgrade your membership.</p>
                  <p>Service Limit: {membershipStatus.servicesUsed}/{membershipStatus.serviceLimit}</p>
                  <p><strong>You need to purchase a membership to add service listings.</strong></p>
                </div>
                <button
                  onClick={() => navigate('/talent/membership')}
                  className="upgrade-btn"
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          ) : !membershipStatus.transactionActive && (
            <div className="membership-banner membership-expired">
              <div className="banner-content">
                <Crown size={24} />
                <div className="banner-text">
                  <h3>Membership Expired</h3>
                  <p>Your membership has expired. Please renew to continue using premium features.</p>
                  <p>Service Limit: {membershipStatus.servicesUsed}/{membershipStatus.serviceLimit}</p>
                </div>
                <button
                  onClick={() => navigate('/talent/membership')}
                  className="upgrade-btn"
                >
                  Renew Now
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Services Grid */}
      {services.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📦</div>
          <h3>No Services Yet</h3>
          <p>Start building your service portfolio by adding your first service</p>
          <button onClick={() => setShowAddModal(true)} className="add-service-btn">
            <Plus size={20} />
            Add Your First Service
          </button>
        </div>
      ) : (
        <div className="services-grid">
          {services.map(service => (
            <div key={service.id} className={`service-card ${!service.isActive ? 'inactive' : ''} ${!membershipStatus.hasPaidMembership ? 'free-account' : ''}`}>
              {service.image && (
                <div className="service-image">
                  <img src={normalizeImageUrl(service.image)} alt={service.title} />
                  {!membershipStatus.hasPaidMembership && (
                    <div className="blur-overlay">
                      <div className="blur-message">
                        <Crown size={20} />
                        <span>Upgrade to remove blur</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="service-header">
                <h3>{service.title}</h3>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {!service.isActive && <span className="inactive-badge">Inactive</span>}
                  {!membershipStatus.hasPaidMembership && <span className="free-badge">Free Account</span>}
                </div>
              </div>

              <p className="service-description">{service.description}</p>

              {service.galleryImages && service.galleryImages.length > 0 && (
                <div className="service-gallery">
                  <span className="gallery-label">Portfolio ({service.galleryImages.length} items)</span>
                  <div className="gallery-thumbnails">
                    {service.galleryImages.slice(0, 3).map((image, index) => {
                      const isVideo = ['.mp4', '.mov', '.avi', '.webm', '.mkv'].some(ext => image.toLowerCase().endsWith(ext));
                      return (
                        <div key={index} className="thumbnail-container">
                          {isVideo ? (
                            <video
                              src={normalizeImageUrl(image)}
                              className="gallery-thumbnail"
                              style={{ objectFit: 'cover' }}
                              muted
                            />
                          ) : (
                            <img
                              src={normalizeImageUrl(image)}
                              alt={`${service.title} portfolio ${index + 1}`}
                              className="gallery-thumbnail"
                            />
                          )}
                          {!membershipStatus.hasPaidMembership && (
                            <div className="thumbnail-blur-overlay"></div>
                          )}
                        </div>
                      );
                    })}
                    {service.galleryImages.length > 3 && (
                      <span className="gallery-more">+{service.galleryImages.length - 3} more</span>
                    )}
                  </div>
                </div>
              )}

              <div className="service-details">
                <div className="detail-item">
                  <DollarSign size={16} />
                  <span>${service.price}/service</span>
                </div>
                <div className="detail-item">
                  <Clock size={16} />
                  <span>{service.duration}</span>
                </div>
                <div className="detail-item">
                  <Tag size={16} />
                  <span>{service.category}</span>
                </div>
              </div>

              <div className="service-actions">
                <button onClick={() => handleEdit(service)} className="edit-btn">
                  <Edit2 size={16} />
                  Edit
                </button>
                <button
                  onClick={() => handleToggleActive(service)}
                  className={`toggle-btn ${service.isActive ? 'active' : 'inactive'}`}
                  title={service.isActive ? 'Deactivate service' : 'Activate service'}
                >
                  {service.isActive ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                  {service.isActive ? 'Active' : 'Inactive'}
                </button>
                <button onClick={() => handleDelete(service.id)} className="delete-btn">
                  <Trash2 size={16} />
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (

        <div className="modal-overlay">
          <div className="service-modal">

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #eee',
                paddingBottom: '12px',
                marginBottom: '16px'
              }}
            >  <h2 className="text-lg font-semibold">
                {editingService ? 'Edit Service' : 'Add New Service'}
              </h2>

              <button
                type="button"
                onClick={resetForm}
                className="flex items-center justify-center w-9 h-9 border-0 bg-transparent hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>


            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Service Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="e.g., Website Development"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe your service..."
                  rows={4}
                  required
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={displayHourlyRate}
                    onChange={(e) => {
                      setDisplayHourlyRate(e.target.checked);
                      if (!e.target.checked) {
                        handleInputChange('price', 0);
                      }
                    }}
                  />
                  <span>Display hourly rate</span>
                </label>
              </div>

              {displayHourlyRate && (
                <div className="form-group">
                  <label>Price ($)</label>
                  <input
                    type="number"
                    value={formData.price}
                    onChange={(e) => handleInputChange('price', parseFloat(e.target.value))}
                    min="0"
                    step="0.01"
                    required
                    placeholder="Enter hourly rate"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Category</label>
                <select
                  value={formData.category}
                  onChange={(e) => handleCategoryChange(e.target.value)}
                  required
                >
                  <option value="">Select a category</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                  <option value="__custom__">➕ Add New Category...</option>
                </select>


              </div>

              <div className="form-group">
                <label>Service Image</label>
                <div className="image-upload-container">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMainImageChange}
                    className="image-input"
                    id="service-image"
                  />
                  <label htmlFor="service-image" className="image-upload-label">
                    <Upload size={20} />
                    Choose Service Image
                  </label>
                  {previewImages.main && (
                    <div className="image-preview">
                      <img src={previewImages.main} alt="Service preview" />
                      <button
                        type="button"
                        onClick={() => {
                          URL.revokeObjectURL(previewImages.main!);
                          setPreviewImages(prev => ({ ...prev, main: null }));
                          setMainImageFile(null);
                        }}
                        className="remove-image-btn"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                  {formData.image && !previewImages.main && (
                    <div className="existing-image">
                      <img src={normalizeImageUrl(formData.image)} alt="Current service" />
                      <span>Current image</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Gallery Images</label>

                {/* Show existing gallery images */}
                {existingGalleryImages.length > 0 && (
                  <div className="existing-gallery-section">
                    <span className="gallery-section-label">Current Gallery Images</span>
                    <div className="gallery-preview">
                      {existingGalleryImages.map((image, index) => (
                        <div key={`existing-gal-${index}`} className="gallery-image-preview">
                          <img
                            src={normalizeImageUrl(image)}
                            alt={`Gallery ${index + 1}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeExistingGalleryImage(image, editingService?.id)}
                            className="remove-image-btn"
                            title="Delete this image"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload new gallery images */}
                <div className="image-upload-container">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleGalleryImagesChange}
                    className="image-input"
                    id="gallery-images"
                  />
                  <label htmlFor="gallery-images" className="image-upload-label">
                    <Image size={20} />
                    Add Gallery Images
                  </label>

                  {/* Show new image previews */}
                  {previewImages.gallery.length > 0 && (
                    <div className="new-gallery-section">
                      <span className="gallery-section-label">New Gallery Images to Upload</span>
                      <div className="gallery-preview">
                        {previewImages.gallery.map((url, index) => (
                          <div key={`new-gal-${index}`} className="gallery-image-preview">
                            <img src={url} alt={`Gallery ${index + 1}`} />
                            <button
                              type="button"
                              onClick={() => removeGalleryImage(index)}
                              className="remove-image-btn"
                              title="Remove this image"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Portfolio (Images & Videos)</label>

                {/* Show existing portfolio items */}
                {existingPortfolio.length > 0 && (
                  <div className="existing-gallery-section">
                    <span className="gallery-section-label">Current Portfolio Items</span>
                    <div className="gallery-preview">
                      {existingPortfolio.map((image, index) => {
                        const isVideo = ['.mp4', '.mov', '.avi', '.webm', '.mkv'].some(ext => image.toLowerCase().endsWith(ext));
                        return (
                          <div key={`existing-port-${index}`} className="gallery-image-preview">
                            {isVideo ? (
                              <video
                                src={normalizeImageUrl(image)}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                                muted
                              />
                            ) : (
                              <img
                                src={normalizeImageUrl(image)}
                                alt={`Portfolio ${index + 1}`}
                              />
                            )}
                            <button
                              type="button"
                              onClick={() => removeExistingPortfolioItem(image, editingService?.id)}
                              className="remove-image-btn"
                              title="Delete this item"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Upload new portfolio items */}
                <div className="image-upload-container">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handlePortfolioChange}
                    className="image-input"
                    id="portfolio-items"
                  />
                  <label htmlFor="portfolio-items" className="image-upload-label">
                    <Image size={20} />
                    Add Portfolio Items (Images & Videos)
                  </label>

                  {/* Show new portfolio previews */}
                  {previewImages.portfolio.length > 0 && (
                    <div className="new-gallery-section">
                      <span className="gallery-section-label">New Portfolio Items to Upload</span>
                      <div className="gallery-preview">
                        {previewImages.portfolio.map((url, index) => {
                          const file = portfolioFiles[index];
                          const isVideo = file?.type?.startsWith('video/') ||
                            ['.mp4', '.mov', '.avi', '.webm', '.mkv'].some(ext => file?.name?.toLowerCase().endsWith(ext));
                          return (
                            <div key={`new-port-${index}`} className="gallery-image-preview">
                              {isVideo ? (
                                <video
                                  src={url}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                                  muted
                                />
                              ) : (
                                <img src={url} alt={`Portfolio ${index + 1}`} />
                              )}
                              <button
                                type="button"
                                onClick={() => removePortfolioItem(index)}
                                className="remove-image-btn"
                                title="Remove this item"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                  />
                  <span>Service is active and available for booking</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={resetForm} className="cancel-btn">
                  <X size={20} />
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={loading || uploadingImages}>
                  <Save size={20} />
                  {uploadingImages ? 'Uploading Images...' : loading ? 'Saving...' : 'Save Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="modal-overlay">
          <div className="service-modal category-modal">
            <h2>Add New Category</h2>

            <form onSubmit={handleCreateCategory}>
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  value={newCategoryData.name}
                  onChange={(e) => setNewCategoryData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Web Development"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newCategoryData.description}
                  onChange={(e) => setNewCategoryData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this category..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Icon (optional)</label>
                <input
                  type="text"
                  value={newCategoryData.icon}
                  onChange={(e) => setNewCategoryData(prev => ({ ...prev, icon: e.target.value }))}
                  placeholder="e.g., Briefcase, Code, Design"
                />
              </div>

              {categoryError && (
                <div className="error-message">
                  {categoryError}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" onClick={resetCategoryModal} className="cancel-btn" disabled={savingCategory}>
                  <X size={20} />
                  Cancel
                </button>
                <button type="submit" className="save-btn" disabled={savingCategory}>
                  <Save size={20} />
                  {savingCategory ? 'Creating...' : 'Create Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TalentServices; 